// TelcoTrack — CETIN RAY-TR3 D5 stand (Libochovany / Lužná layout).
// Measured from D5_1_Osazeni_RAYTR_RSU_Libochovany + Google Sheet dump:
// 27 cols A–AA, 2-col OFSM blocks, 3-row port cells.
// Opaque 3U = 16 rows, 2U = 11, 1U = 5; splitter 1U = 6; jumper 1U = 5; OFSM 3U = 36.

import type {
  RozvadecOccupancyPortCell,
  RozvadecOccupancySheet,
  RozvadecOccupancySheetLayout,
} from './rozvadec-occupancy-schema.types';

export const RAY_D5_COL_COUNT = 27;
export const RAY_D5_HEADER_ROWS = 6;
export const RAY_D5_OFSM_COLUMNS = 12;
export const RAY_D5_OFSM_PORT_ROWS = 12;
export const RAY_D5_OFSM_TRIPLET = 3;
export const RAY_D5_U3_OPAQUE_ROWS = 16;
export const RAY_D5_U2_OPAQUE_ROWS = 11;
export const RAY_D5_U1_OPAQUE_ROWS = 5;
export const RAY_D5_U1_SPLITTER_ROWS = 6;
export const RAY_D5_U1_JUMPER_ROWS = 5;
export const RAY_D5_OFSM_ROWS = 1 + RAY_D5_OFSM_PORT_ROWS * RAY_D5_OFSM_TRIPLET;

/** Excel character widths from the Libochovany stand (A3 portrait). */
export const RAY_D5_COL_A_WIDTH = 2.2;
export const RAY_D5_COL_B_WIDTH = 11.2;
export const RAY_D5_COL_C_WIDTH = 13.4;
export const RAY_D5_COL_ODF_WIDTH = 3.7;

export const RAY_D5_ROW_H_HEADER = 14;
export const RAY_D5_ROW_H_OPAQUE = 22;
export const RAY_D5_ROW_H_OFSM = 10;
export const RAY_D5_ROW_H_SPLITTER = 18;
export const RAY_D5_ROW_H_JUMPER = 22;

export const RAY_D5_FILL_HEADER = 'FFC6EFCE';
export const RAY_D5_FILL_OCCUPIED = 'FFD9EAD3';
export const RAY_D5_FILL_FREE = 'FFFFFFFF';
export const RAY_D5_FILL_REZERVA = 'FFEEEEEE';
export const RAY_D5_FILL_DEST = 'FFFFF2CC';
export const RAY_D5_FILL_PORT = 'FFFFFFFF';
export const RAY_D5_FILL_JUMPER = 'FFD0E2F3';
export const RAY_D5_FILL_SPLITTER = 'FFFCE5CD';

export type RayD5Grid = {
  aoa: (string | number)[][];
  layout: RozvadecOccupancySheetLayout;
};

function emptyRow(): string[] {
  return Array.from({ length: RAY_D5_COL_COUNT }, () => '');
}

function merge(
  layout: RozvadecOccupancySheetLayout,
  startRow: number,
  startCol: number,
  endRow: number,
  endCol: number,
): void {
  if (endRow < startRow || endCol < startCol) return;
  if (startRow === endRow && startCol === endCol) return;
  layout.merges.push({ startRow, startCol, endRow, endCol });
}

function fillRect(
  layout: RozvadecOccupancySheetLayout,
  startRow: number,
  startCol: number,
  endRow: number,
  endCol: number,
  argb: string,
): void {
  for (let row = startRow; row <= endRow; row += 1) {
    for (let col = startCol; col <= endCol; col += 1) {
      layout.fills.push({ row, col, argb });
    }
  }
}

export function rayD5OpaqueRows(heightU: number): number {
  if (!Number.isInteger(heightU) || heightU <= 0) return RAY_D5_U1_OPAQUE_ROWS;
  let left = heightU;
  let rows = 0;
  while (left >= 3) {
    rows += RAY_D5_U3_OPAQUE_ROWS;
    left -= 3;
  }
  if (left === 2) rows += RAY_D5_U2_OPAQUE_ROWS;
  if (left === 1) rows += RAY_D5_U1_OPAQUE_ROWS;
  return rows;
}

/** Compact CETIN stand targets: `č.p.262` / `p.č.847/31` / `rezerva` / `OK 480.LBA`. */
export function compactRayD5Destination(raw: string | null | undefined): string {
  const text = String(raw ?? '').trim();
  if (!text || text === '—' || text === 'volno' || /^voln/i.test(text)) return 'rezerva';
  if (/^rez\.?a?r?v?a?$/i.test(text) || text === 'rez.') return 'rezerva';
  const cp = text.match(/č\.?\s*p\.?\s*([0-9]+(?:\/[0-9]+)?)/i);
  if (cp) return `č.p.${cp[1]}`;
  const parcel = text.match(/(?:p\.?\s*č\.?|parc(?:ela|\.)?)\s*([0-9]+\/[0-9]+)/i);
  if (parcel) return `p.č.${parcel[1]}`;
  if (/^OK\b/i.test(text)) return text.replace(/^ok\s+/i, 'OK ');
  return text;
}

export function formatRaySplitterStandLabel(count: number, ratio: string | null | undefined): string {
  const out = ratio === '1:16' || ratio === '1/16' ? '16' : '8';
  return `${count} splitter 1/${out}`;
}

function ofsmColumnCount(ports: readonly RozvadecOccupancyPortCell[], hinted?: number): number {
  if (hinted && hinted > 0) return hinted;
  if (ports.length >= 144) return 12;
  if (ports.length >= 96) return 8;
  if (ports.length >= 48) return 4;
  return Math.max(1, Math.ceil(ports.length / RAY_D5_OFSM_PORT_ROWS));
}

function writeHeader(
  aoa: (string | number)[][],
  layout: RozvadecOccupancySheetLayout,
  title: string,
  cabinetLabel: string,
): void {
  aoa[0][1] = title;
  merge(layout, 1, 2, 1, RAY_D5_COL_COUNT);
  fillRect(layout, 1, 2, 1, RAY_D5_COL_COUNT, RAY_D5_FILL_HEADER);

  aoa[1][1] = 'Pozice 3U modulu';
  aoa[1][2] = 'Pozice splitteru';
  aoa[1][3] = 'ODF osazení/adresace';
  merge(layout, 2, 2, 6, 2);
  merge(layout, 2, 3, 6, 3);
  merge(layout, 2, 4, 2, RAY_D5_COL_COUNT);
  fillRect(layout, 2, 2, 6, RAY_D5_COL_COUNT, RAY_D5_FILL_HEADER);

  aoa[2][3] = 'Skříň';
  merge(layout, 3, 4, 4, RAY_D5_COL_COUNT);

  aoa[4][3] = cabinetLabel;
  merge(layout, 5, 4, 5, RAY_D5_COL_COUNT);

  for (let block = 0; block < RAY_D5_OFSM_COLUMNS; block += 1) {
    const col = 4 + block * 2;
    aoa[5][col - 1] = block + 1;
    merge(layout, 6, col, 6, col + 1);
  }
}

function writeOpaque(
  aoa: (string | number)[][],
  layout: RozvadecOccupancySheetLayout,
  startRow0: number,
  rows: number,
  kind: 'occupied' | 'free' | 'jumper' | 'splitter',
  leftLabel: string,
  bodyLabel: string,
): void {
  const start = startRow0 + 1;
  const end = startRow0 + rows;
  const fill =
    kind === 'occupied'
      ? RAY_D5_FILL_OCCUPIED
      : kind === 'free'
        ? RAY_D5_FILL_FREE
        : kind === 'jumper'
          ? RAY_D5_FILL_JUMPER
          : RAY_D5_FILL_SPLITTER;
  if (kind === 'free') {
    aoa[startRow0][2] = bodyLabel;
    merge(layout, start, 3, end, RAY_D5_COL_COUNT);
  } else if (kind === 'jumper') {
    aoa[startRow0][1] = bodyLabel;
    merge(layout, start, 2, end, RAY_D5_COL_COUNT);
  } else if (kind === 'splitter') {
    aoa[startRow0][2] = bodyLabel;
    merge(layout, start, 3, end, RAY_D5_COL_COUNT);
  } else {
    if (leftLabel) aoa[startRow0][1] = leftLabel;
    aoa[startRow0][3] = bodyLabel;
    if (leftLabel) merge(layout, start, 2, end, 2);
    merge(layout, start, 3, end, 3);
    merge(layout, start, 4, end, RAY_D5_COL_COUNT);
  }
  fillRect(layout, start, 2, end, RAY_D5_COL_COUNT, fill);
  const height =
    kind === 'splitter' ? RAY_D5_ROW_H_SPLITTER : kind === 'jumper' ? RAY_D5_ROW_H_JUMPER : RAY_D5_ROW_H_OPAQUE;
  for (let row = start; row <= end; row += 1) layout.rowHeights[row - 1] = height;
}

function writeOfsm(
  aoa: (string | number)[][],
  layout: RozvadecOccupancySheetLayout,
  startRow0: number,
  typeLabel: string,
  routeLabel: string,
  ports: readonly RozvadecOccupancyPortCell[],
  columnCount: number,
): void {
  const start = startRow0 + 1;
  const end = startRow0 + RAY_D5_OFSM_ROWS;
  aoa[startRow0][1] = typeLabel;
  aoa[startRow0][2] = routeLabel;
  merge(layout, start, 2, end, 2);
  merge(layout, start, 3, end, 3);
  fillRect(layout, start, 2, end, 3, RAY_D5_FILL_HEADER);

  for (let col = 0; col < columnCount; col += 1) {
    const excelCol = 4 + col * 2;
    const topPort = col * RAY_D5_OFSM_PORT_ROWS + 1;
    aoa[startRow0][excelCol - 1] = topPort;
    merge(layout, start, excelCol, start, excelCol + 1);
    fillRect(layout, start, excelCol, start, excelCol + 1, RAY_D5_FILL_HEADER);
    for (let row = 0; row < RAY_D5_OFSM_PORT_ROWS; row += 1) {
      const port = ports[col * RAY_D5_OFSM_PORT_ROWS + row];
      const r0 = startRow0 + 1 + row * RAY_D5_OFSM_TRIPLET;
      const excelRow = r0 + 1;
      const position = port?.printLabel || String(topPort + row);
      const fiber = port && port.source && port.source !== '—' ? port.source : '';
      const dest = compactRayD5Destination(port?.target);
      const missing = !port || port.status === 'neexistuje';
      aoa[r0][excelCol - 1] = missing ? '' : position;
      aoa[r0 + 1][excelCol - 1] = missing ? '' : fiber;
      aoa[r0 + 2][excelCol - 1] = missing ? '' : dest;
      merge(layout, excelRow, excelCol, excelRow, excelCol + 1);
      merge(layout, excelRow + 1, excelCol, excelRow + 1, excelCol + 1);
      merge(layout, excelRow + 2, excelCol, excelRow + 2, excelCol + 1);
      const destFill =
        missing || dest === ''
          ? RAY_D5_FILL_PORT
          : dest === 'rezerva'
            ? RAY_D5_FILL_REZERVA
            : RAY_D5_FILL_DEST;
      fillRect(layout, excelRow, excelCol, excelRow + 1, excelCol + 1, RAY_D5_FILL_PORT);
      fillRect(layout, excelRow + 2, excelCol, excelRow + 2, excelCol + 1, destFill);
    }
  }
  for (let row = start; row <= end; row += 1) layout.rowHeights[row - 1] = RAY_D5_ROW_H_OFSM;
}

export function buildRayTr3D5Grid(sheet: RozvadecOccupancySheet): RayD5Grid {
  const title = sheet.objectName || sheet.printName || 'RSU';
  const cabinetLabel =
    sheet.rowLabel && sheet.rowLabel !== 'stojan' ? sheet.rowLabel : title;
  const aoa: (string | number)[][] = Array.from({ length: RAY_D5_HEADER_ROWS }, () => emptyRow());
  const layout: RozvadecOccupancySheetLayout = {
    merges: [],
    columnWidths: [
      RAY_D5_COL_A_WIDTH,
      RAY_D5_COL_B_WIDTH,
      RAY_D5_COL_C_WIDTH,
      ...Array.from({ length: RAY_D5_OFSM_COLUMNS * 2 }, () => RAY_D5_COL_ODF_WIDTH),
    ],
    rowHeights: Array.from({ length: RAY_D5_HEADER_ROWS }, () => RAY_D5_ROW_H_HEADER),
    pageOrientation: 'portrait',
    pageSize: 'A3',
    fills: [],
  };
  writeHeader(aoa, layout, title, cabinetLabel || title);

  for (const slot of sheet.positions.filter((item) => item.render)) {
    const startRow0 = aoa.length;
    const cassette = slot.cassette;
    const heightU = cassette?.heightU ?? 3;
    if (!cassette || slot.empty || cassette.kind === 'empty') {
      const rows = rayD5OpaqueRows(heightU);
      for (let i = 0; i < rows; i += 1) aoa.push(emptyRow());
      writeOpaque(aoa, layout, startRow0, rows, 'free', '', 'Volná pozice');
      continue;
    }
    if (cassette.kind === 'JUMPER') {
      for (let i = 0; i < RAY_D5_U1_JUMPER_ROWS; i += 1) aoa.push(emptyRow());
      writeOpaque(aoa, layout, startRow0, RAY_D5_U1_JUMPER_ROWS, 'jumper', '', 'Jumper storage 1U');
      continue;
    }
    if (cassette.kind === 'SPLITTER_MODULE') {
      const rows = Math.max(RAY_D5_U1_SPLITTER_ROWS, heightU === 1 ? RAY_D5_U1_SPLITTER_ROWS : rayD5OpaqueRows(heightU));
      for (let i = 0; i < rows; i += 1) aoa.push(emptyRow());
      writeOpaque(aoa, layout, startRow0, rows, 'splitter', '', cassette.printLabel);
      continue;
    }
    if (cassette.kind === 'OFSM') {
      for (let i = 0; i < RAY_D5_OFSM_ROWS; i += 1) aoa.push(emptyRow());
      writeOfsm(
        aoa,
        layout,
        startRow0,
        cassette.printLabel || 'RAY-OFSM-3U',
        cassette.routeLabel || '',
        cassette.ports,
        ofsmColumnCount(cassette.ports, cassette.columnCount),
      );
      continue;
    }
    const rows = rayD5OpaqueRows(heightU);
    for (let i = 0; i < rows; i += 1) aoa.push(emptyRow());
    writeOpaque(
      aoa,
      layout,
      startRow0,
      rows,
      'occupied',
      slot.scaleLabels[0] != null ? String(slot.scaleLabels[0]) : '',
      cassette.printLabel || 'Obsazená pozice',
    );
  }

  return { aoa, layout };
}
